<div class="main-view space-y-2 md:space-y-4">
  <app-page-surface-header
    titleIcon="file"
    title="Product documents"
    [subtitle]="'Choose which documents ' + slug + ' collects, whether they are required, and the capture order.'"
  >
    <ng-container ngProjectAs="[pageHeaderActions]">
      <!-- Neither control uses `.btn`: it carries its own padding, a 1px currentColor border and a
           drop shadow, which the two buttons cancelled to different degrees and left them looking
           mismatched. One fixed height and identical box classes keep them on a single line.
           icon-arrow-left draws a RIGHT-pointing arrow despite its name; icon-arrow-backward is
           the actual left chevron. -->
      <a
        routerLink="/products"
        class="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium leading-none text-black shadow-none transition hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
      >
        <icon-arrow-backward class="size-4 shrink-0" />
        <span>Back</span>
      </a>
      <button
        type="button"
        class="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-full border border-transparent bg-[#303030] px-5 text-sm font-medium leading-none text-white shadow-none transition hover:bg-black disabled:opacity-50 dark:bg-gray-900 dark:hover:bg-black"
        [disabled]="saving() || loading()"
        (click)="save()"
      >
        {{ saving() ? 'Saving…' : 'Save changes' }}
      </button>
    </ng-container>
  </app-page-surface-header>

  <div
    class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100"
    data-testid="rollout-notice"
  >
    {{ rolloutNotice }}
  </div>

  @if (error()) {
  <div
    class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
    role="alert"
  >
    {{ error() }}
  </div>
  }

  @if (notice() && !error()) {
  <output
    class="block rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-300"
  >
    {{ notice() }}
  </output>
  }

  <app-list-module-surface
    [config]="listSurfaceConfig"
    [filters]="listFilters"
    [errorMessage]="''"
    [loading]="loading()"
    [meta]="paginationMeta"
    [rowCount]="pagedRecords.length"
    (filtersChange)="onListFiltersChange($event)"
    (pageChange)="onListPageChange($event)"
  >
    <app-list-module-table
      listTable
      variant="surface"
      [columns]="columns"
      [rows]="pagedRecords"
      [loading]="loading()"
      [emptyMessage]="emptyMessage"
      rowTrackKey="code"
    >
      <ng-template listCellDef="selected" let-row="row">
        <label
          class="relative inline-flex h-6 w-11 items-center"
          [attr.for]="'doc-selected-' + row.code"
          [class.cursor-pointer]="!isLocked(row.code)"
          [class.cursor-not-allowed]="isLocked(row.code)"
          [class.opacity-50]="isLocked(row.code)"
        >
          <input
            type="checkbox"
            class="peer sr-only"
            [id]="'doc-selected-' + row.code"
            [attr.aria-label]="'Collect ' + row.display_name"
            [checked]="row.selected"
            [disabled]="isLocked(row.code)"
            (change)="toggleSelected(row.code)"
          />
          <span class="h-6 w-11 rounded-full bg-gray-200 transition peer-checked:bg-[#303030] dark:bg-white/10"></span>
          <span
            class="absolute left-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5"
          ></span>
        </label>
      </ng-template>

      <ng-template listCellDef="document" let-row="row">
        <div class="font-medium text-gray-900 dark:text-white">{{ row.display_name }}</div>
        <div class="font-mono text-[11px] text-gray-500 dark:text-gray-400">{{ row.code }}</div>
        @if (row.hint) {
        <div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{{ row.hint }}</div>
        }
        @if (isLocked(row.code)) {
        <div class="mt-0.5 text-xs text-amber-700 dark:text-amber-300">Always collected — proves the customer is live</div>
        }
      </ng-template>

      <ng-template listCellDef="required" let-row="row">
        <!-- "Required" only means anything once the product collects the document, so the toggle
             is inert and reads off until Collected is on. -->
        <label
          class="relative inline-flex h-6 w-11 items-center"
          [attr.for]="'doc-required-' + row.code"
          [attr.title]="row.selected ? null : 'Collect this document first'"
          [class.cursor-pointer]="canEditRequired(row)"
          [class.cursor-not-allowed]="!canEditRequired(row)"
          [class.opacity-50]="!canEditRequired(row)"
        >
          <input
            type="checkbox"
            class="peer sr-only"
            [id]="'doc-required-' + row.code"
            [attr.aria-label]="'Require ' + row.display_name"
            [checked]="row.selected && row.required"
            [disabled]="!canEditRequired(row)"
            (change)="toggleRequired(row.code)"
          />
          <span class="h-6 w-11 rounded-full bg-gray-200 transition peer-checked:bg-[#303030] dark:bg-white/10"></span>
          <span
            class="absolute left-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5"
          ></span>
        </label>
      </ng-template>

      <ng-template listCellDef="order" let-row="row">
        <input
          type="number"
          class="form-input w-20 rounded-xl border-gray-200 bg-white dark:border-[#191e3a] dark:bg-[#0e1726]"
          [value]="row.order"
          [disabled]="isLocked(row.code)"
          (change)="setOrder(row.code, $any($event.target).valueAsNumber)"
        />
      </ng-template>

      <ng-template listCellDef="capture_modes" let-row="row">
        <div class="flex flex-wrap gap-1">
          @for (mode of row.capture_modes; track mode) {
          <span
            class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-white/10 dark:text-gray-300"
            >{{ mode }}</span
          >
          }
        </div>
      </ng-template>
    </app-list-module-table>
  </app-list-module-surface>
</div>
